/**
 * Wall boundary geometry for collision checks, demo-aware.
 *
 * Splits a wall into its STANDING (non-demolished) sub-segments so demolished
 * portions don't act as walls, and builds the outward-extruded OBB for an
 * arbitrary sub-segment. A demo'ed portion is the opening subtype
 * `category.opening.demoed` (detected via isDemoedOpening), parented via
 * `parentWallIds`, positioned by `center` + width `dimensions.x` — i.e. a
 * `[start, end]` span along the wall axis.
 * Subtracting those spans from the wall yields the standing pieces.
 *
 * Pure math; no I/O. `subtractSpans` is shared with kitchen-assembly's island
 * clearance (islandWallClearance.js).
 *
 * NOTE: a separate concern — `kitchen-assembly/wallSegments.js` computes
 * available cabinet-run ranges (subtracts ALL openings) — and also carries an
 * inline interval-subtract. Folding that into `subtractSpans` here is a
 * possible future DRY follow-up, out of scope for this change.
 *
 * See design: claude-docs/2026-06-12-collision-exclude-demoed-walls.md
 */

const { wallToObb } = require('../bathroom-analyzer/pipeline/geometry');

// Standing pieces shorter than this (float-noise slivers between adjacent
// demo gaps) are dropped.
const MIN_STANDING_SEGMENT_LEN = 0.01; // 10mm

// Max perpendicular distance from an opening center to the wall axis for a
// legacy opening (no usable parentWallIds) to count as "on this wall".
const ON_WALL_PERP_TOLERANCE = 0.3;

/**
 * Wall thickness used when extruding the boundary OBB for SAT. Floor-plan
 * walls are stored zero-thickness; fall back to 50mm (the wallToObb default),
 * which must exceed the FLOORPLAN tolerance (~3.2mm) used by overlapAmount or
 * a thinner OBB's overlap is filtered out as numerical noise.
 */
function getWallBoundaryThickness(wall) {
  const stored = wall?.thickness || wall?.dimensions?.z || wall?.depth || 0;
  return Math.max(stored, 0.05);
}

/**
 * Normalize a floor-plan wall into world-space endpoints. Floor-plan walls
 * store `alignment.bottomLeft/bottomRight`; structure walls / snapshots only
 * carry `center + dimensions + rotation` — derive endpoints from those.
 * @returns {{start:{x,z}, end:{x,z}}|null}
 */
function getWallEndpoints(wall) {
  let start = wall.start || wall.alignment?.bottomLeft;
  let end = wall.end || wall.alignment?.bottomRight;
  if ((!start || !end) && wall.center && wall.dimensions) {
    const halfLength = (wall.dimensions.x || 0) / 2;
    if (halfLength > 0) {
      const rotY = wall.rotation?.y || 0;
      const cos = Math.cos(rotY);
      const sin = Math.sin(rotY);
      start = {
        x: wall.center.x - cos * halfLength,
        z: wall.center.z + sin * halfLength,
      };
      end = {
        x: wall.center.x + cos * halfLength,
        z: wall.center.z - sin * halfLength,
      };
    }
  }
  if (!start || !end) return null;
  return { start, end };
}

/**
 * Inward normal of a wall edge start→end (CCW room polygon): (-dz, dx)/len.
 * Prefers a stored `wall.normal`.
 * @returns {{x,z}|null}
 */
function getWallNormal(start, end, wall) {
  if (wall?.normal) return wall.normal;
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-10) return null;
  return { x: -dz / len, z: dx / len };
}

/**
 * Build a finite, outward-extruded OBB for an arbitrary sub-segment of a wall
 * (explicit endpoints so each standing sub-segment gets its own OBB).
 * @returns {Object|null}
 */
function segmentToBoundaryObb(start, end, wall) {
  const normal = getWallNormal(start, end, wall);
  if (!normal) return null;
  return wallToObb({ start, end, normal }, getWallBoundaryThickness(wall));
}

/**
 * Merge overlapping/adjacent spans, then subtract them from [0, total].
 * Spans are `[start, end]` along the wall axis. Returns the remaining solid
 * `[start, end]` intervals. (Shared with kitchen-assembly island clearance.)
 */
function subtractSpans(total, spans) {
  if (spans.length === 0) return total > 0 ? [[0, total]] : [];
  const sorted = spans
    .map(([s, e]) => [
      Math.max(0, Math.min(s, e)),
      Math.min(total, Math.max(s, e)),
    ])
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [s, e] of sorted) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  }
  const solid = [];
  let cursor = 0;
  for (const [s, e] of merged) {
    if (s > cursor) solid.push([cursor, s]);
    cursor = Math.max(cursor, e);
  }
  if (cursor < total) solid.push([cursor, total]);
  return solid;
}

/**
 * The demo'ed `[start, end]` spans (along the wall axis) for one wall.
 * Selects demo openings by `parentWallIds`; for legacy openings with no
 * usable parentWallIds, falls back to perpendicular-distance matching.
 */
function demoSpansForWall(wall, demoOpenings, axes) {
  const { start, dir, normal, length } = axes;
  const spans = [];
  for (const op of demoOpenings) {
    const c = op?.center;
    const w = op?.dimensions?.x;
    if (!c || !(w > 0)) continue;

    const ids = Array.isArray(op.parentWallIds) ? op.parentWallIds : [];
    if (ids.length > 0) {
      if (!ids.includes(wall.id)) continue; // belongs to a different wall
    } else {
      const perp = Math.abs(
        (c.x - start.x) * normal.x + (c.z - start.z) * normal.z
      );
      if (perp > ON_WALL_PERP_TOLERANCE) continue; // not on this wall
    }

    const t = (c.x - start.x) * dir.x + (c.z - start.z) * dir.z;
    const s = Math.max(0, t - w / 2);
    const e = Math.min(length, t + w / 2);
    if (e > s) spans.push([s, e]);
  }
  return spans;
}

/**
 * The STANDING (non-demolished) sub-segments of a wall, as world-space
 * endpoints. A wall with no demo openings returns a single full-length
 * segment (identical to pre-demo behavior). A fully demolished wall → [].
 *
 * @param {Object} wall
 * @param {Array<Object>} [demoOpenings=[]] - demoed openings (the opening
 *   subtype category.opening.demoed), pre-filtered by the caller
 * @returns {Array<{start:{x,z}, end:{x,z}, length:number}>}
 */
function standingWallSegments(wall, demoOpenings = []) {
  const ep = getWallEndpoints(wall);
  if (!ep) return [];
  const { start, end } = ep;
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const length = Math.hypot(dx, dz);
  if (length < 1e-10) return [];
  const dir = { x: dx / length, z: dz / length };
  const normal = { x: -dz / length, z: dx / length };

  const demoSpans = demoOpenings.length
    ? demoSpansForWall(wall, demoOpenings, { start, dir, normal, length })
    : [];
  const solid = subtractSpans(length, demoSpans);

  const segments = [];
  for (const [s, e] of solid) {
    if (e - s < MIN_STANDING_SEGMENT_LEN) continue;
    segments.push({
      start: { x: start.x + s * dir.x, z: start.z + s * dir.z },
      end: { x: start.x + e * dir.x, z: start.z + e * dir.z },
      length: e - s,
    });
  }
  return segments;
}

module.exports = {
  getWallBoundaryThickness,
  getWallEndpoints,
  getWallNormal,
  segmentToBoundaryObb,
  subtractSpans,
  standingWallSegments,
};
